'use client'

import { useEffect } from 'react'

// Signature Next/webpack use for a stale-chunk fetch after a redeploy moves
// the content hashes out from under a tab that's still open. Kept in sync
// with the same check in app/(dashboard)/error.tsx, app/(admin)/error.tsx,
// and app/global-error.tsx.
const CHUNK_ERROR_PATTERN = /Loading chunk|Loading CSS chunk|error loading dynamically imported module|Failed to fetch dynamically imported module/i
// Renamed from the old bare-timestamp key so a tab still holding that value
// can't get JSON-parsed as a number and read as a truthy, wrong-shaped guard.
const CHUNK_RELOAD_KEY = 'halo-core:chunk-reload'
// Attempt counter, not a recency window: bounds automatic reloads absolutely
// regardless of how long each cycle takes to fail again. A genuine skew
// incident gets up to two automatic tries; a third failure means it isn't
// skew and the boundary renders instead of looping.
const CHUNK_RELOAD_MAX_ATTEMPTS = 2
// A fresh incident (a later, unrelated deploy) resets the counter instead of
// inheriting an old one. Keeping the original `t` on every write, not this
// constant, is what keeps the window from sliding forward on each reload.
const CHUNK_RELOAD_RESET_MS = 60000

function isChunkLoadError(error: Error): boolean {
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_PATTERN.test(error.message ?? '')
}

interface ChunkReloadGuard { n: number; t: number }

// sessionStorage throws in a sandboxed iframe without allow-same-origin, and
// in some privacy configurations. `null` means storage itself is
// unavailable (fail closed, caller must not auto-reload); it is never
// returned for a merely missing or malformed value, those are a fresh
// incident, not a failure.
function readGuard(): ChunkReloadGuard | null {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(CHUNK_RELOAD_KEY)
  } catch {
    return null
  }
  if (!raw) return { n: 0, t: Date.now() }
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.n === 'number' && typeof parsed.t === 'number') {
      return parsed as ChunkReloadGuard
    }
  } catch {
    // Falls through to the fresh default below.
  }
  return { n: 0, t: Date.now() }
}

function writeGuard(value: ChunkReloadGuard): boolean {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

function clearGuard(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // Best-effort. A throw here must not take out the Try again button.
  }
}

// Especially plain: this is what someone who can't get in sees, so it stays
// to the point rather than adding extra explanation they can't act on.
export default function AuthError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    if (!isChunkLoadError(error)) return
    const guard = readGuard()
    if (guard === null) return // storage unavailable, fail closed, no auto-reload
    const fresh = Date.now() - guard.t > CHUNK_RELOAD_RESET_MS
    const current = fresh ? { n: 0, t: Date.now() } : guard
    if (current.n >= CHUNK_RELOAD_MAX_ATTEMPTS) return // suppressed, boundary renders
    // Write before reload, never after: reload tears down the page, so
    // anything written after it never executes.
    if (writeGuard({ n: current.n + 1, t: current.t })) {
      window.location.reload()
    }
  }, [error])

  const handleReset = () => {
    clearGuard()
    reset()
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-halo-canvas p-6">
      <div className="max-w-sm w-full text-center space-y-3">
        <h2 className="text-lg font-medium text-halo-ink">Something went wrong</h2>
        <p className="text-sm text-halo-secondary">Try again, or reload the page.</p>

        {error.digest && (
          <code className="block px-2.5 py-1.5 rounded-sm bg-halo-inset font-mono text-xs text-halo-secondary select-all break-all">
            {error.digest}
          </code>
        )}

        {process.env.NODE_ENV !== 'production' && error.message && (
          <code className="block px-2.5 py-1.5 rounded-sm bg-halo-inset font-mono text-xs text-halo-danger break-all">
            {error.message}
          </code>
        )}

        <div className="flex items-center justify-center gap-2 pt-1">
          <button onClick={handleReset} className="btn-primary">Try again</button>
          <button onClick={() => window.location.reload()} className="btn-secondary">Reload page</button>
        </div>
      </div>
    </div>
  )
}
